import { useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { Sheet } from './UI'
import { safeStorage } from '../lib/safeStorage'
import { logEvent } from '../lib/analytics'
import { isOverlayBusy } from '../lib/overlayBusy'
import { withTimeout } from '../lib/withTimeout'
import { noteMark, claimAsk } from '../lib/askQueue'

// ── PushAfterFirstMark — ask for notifications when they first mean something ──
// 2026-10-12. The permission ask used to live only in /start's notify step, so
// anyone who arrived by signup, import or a shared link never saw it; push opt-in
// sat at 19/146 (retention read 09-30) and it is the bottleneck for every
// reminder rail. The moment that earns the ask is the first episode mark: you
// have just told us where you are in a show, so "hear when the next one airs" is
// a concrete offer, not a permission grab on a cold screen.
//
// Rules: once per account (storage), only when the browser can still ask
// (permission 'default', PushManager present), never over another overlay, and
// a few seconds after the mark so the Undo bar and the Episode Pulse nudge (both
// contextual, both short-lived) land first and the ask never stacks on them;
// while it is open it holds the modal flag so later rails queue behind it. The
// whole thing is a bottom sheet, not the native prompt: a "Not now" costs nothing and the native
// prompt only opens behind an explicit "Turn on".
const KEY = (uid) => `nextep_push_ask_${uid}`

export function pushAskState() {
  try {
    if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) return 'unsupported'
    return Notification.permission === 'default' ? 'ask' : Notification.permission
  } catch { return 'unsupported' }
}

export function PushAfterFirstMark({ user, delay = 5500 }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const timer = useRef(null)
  const uid = user?.id

  useEffect(() => {
    if (!uid) return
    const onMark = (e) => {
      const markId = noteMark(e); if (!markId) return
      if (safeStorage.get(KEY(uid))) return
      if (pushAskState() !== 'ask') return
      if (timer.current) return
      const tryOpen = (left) => {
        if (isOverlayBusy() && left > 0) { timer.current = setTimeout(() => tryOpen(left - 1), 700); return }
        timer.current = null
        if (isOverlayBusy()) return   // still busy after the retries: skip, the next mark asks again
        if (!claimAsk(markId)) return   // one ask per mark (lib/askQueue)
        safeStorage.set(KEY(uid), new Date().toISOString())
        logEvent('push_after_mark', { result: 'shown' })
        setOpen(true)
      }
      timer.current = setTimeout(() => tryOpen(8), delay)
    }
    window.addEventListener('nextep:progress-marked', onMark)
    return () => { window.removeEventListener('nextep:progress-marked', onMark); if (timer.current) clearTimeout(timer.current); timer.current = null }
  }, [uid, delay])

  useEffect(() => {
    if (!open) return
    window.__nextepModalHold = true
    return () => { window.__nextepModalHold = false }
  }, [open])

  if (!open) return null

  const close = (result) => { setOpen(false); if (result) logEvent('push_after_mark', { result }) }
  const turnOn = async () => {
    if (busy) return
    setBusy(true)
    let perm = null
    try {
      const push = await import('../lib/push')
      perm = await push.requestPermission()
      // The subscription can wait on serviceWorker.ready; on a slow or dev worker that
      // never settles. Permission is the part that matters: ensureFreshSubscription
      // repairs a missing subscription on the next boot.
      if (perm === 'granted') await withTimeout(push.subscribeToPush(uid), 6000, 'push subscribe').catch(() => {})
    } catch {}
    setBusy(false)
    close(perm === 'granted' ? 'granted' : perm === 'denied' ? 'denied' : 'failed')
    window.dispatchEvent(new CustomEvent('nextep:toast', { detail: perm === 'granted'
      ? { title: '🔔 Reminders on', accent: 'var(--amber)', body: 'The night before and the day of, for the shows on your list. Change it any time in Settings → Notifications.' }
      : { title: 'Notifications stayed off', body: 'You can turn them on later in Settings → Notifications.' } }))
  }

  return (
    <Sheet open onClose={() => close('dismissed')} label="Hear when the next one airs?" from="bottom">
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-full)', margin: '0 auto 10px', background: 'rgba(var(--amber-rgb),0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Bell size={24} color="var(--amber)" />
        </div>
        <div style={{ color: 'var(--text-strong)', fontFamily: 'var(--font-serif)', fontSize: 22, lineHeight: 1.15 }}>Hear when the next one airs?</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>
          One nudge the night before and one on the day, only for the shows on your list. Nothing else.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => close('dismissed')} style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontWeight: 600, fontSize: 15, padding: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}>Not now</button>
        <button onClick={turnOn} disabled={busy} style={{ flex: 1.2, background: 'var(--amber)', border: 'none', borderRadius: 'var(--radius-md)', color: 'var(--amber-ink)', fontWeight: 700, fontSize: 15, padding: 13, cursor: busy ? 'default' : 'pointer', fontFamily: 'var(--font)', opacity: busy ? 0.7 : 1 }}>{busy ? 'Asking…' : 'Turn on'}</button>
      </div>
    </Sheet>
  )
}
